import { MicOff, X } from 'lucide-react'
import { useRecordingStore } from '@/stores/recording'

/**
 * The recording store's terminal `error`, on screen.
 *
 * This existed once. `RecordingBanner` rendered it, `AppShell` mounted it, and
 * the note-first migration (8ec9cc5) removed the mount along with the transport
 * controls that had moved into the notes column header. The error kept being
 * set and stopped being shown to anyone — `PendingUploadBanner`'s own docstring
 * records the symptom in passing ("a red message, in a banner that is not even
 * mounted") and routes around it for the upload case only.
 *
 * What that cost: pressing record with the microphone busy or blocked set
 * `error` to "Permission denied", reset the UI to idle, and displayed nothing.
 * The doctor saw a note appear in the list reading "Recording — open to check
 * or finish" and had every reason to believe the consult was being captured.
 *
 * Deliberately narrow. It renders the terminal error and nothing else:
 *  - transport (timer, pause, stop) belongs to `NoteRecordingControls`;
 *  - the live silence nudge (`audioWarning`) is already rendered there and in
 *    `RecordingFAB` while state is `recording`.
 * Re-mounting the whole original banner would duplicate both.
 */
export function RecordingErrorBanner() {
  const error = useRecordingStore((s) => s.error)
  const dismissError = useRecordingStore((s) => s.dismissError)

  if (!error) return null

  return (
    <div
      role="alert"
      className="shrink-0 bg-error/10 border-b border-error/25 px-4 py-2 flex items-center gap-3"
    >
      <MicOff className="w-4 h-4 text-error shrink-0" aria-hidden />
      <p className="flex-1 text-[13px] text-on-error-soft">{error}</p>
      <button
        onClick={dismissError}
        className="p-1 rounded text-on-error-soft hover:text-on-error-soft hover:bg-error/10 transition-colors shrink-0"
        aria-label="Dismiss recording error"
      >
        <X className="w-3.5 h-3.5" aria-hidden />
      </button>
    </div>
  )
}
